/**
 * The rules that turn sharpness numbers into the two markers a photographer sees.
 * Pure module: no imports, no I/O.
 *
 * Both rules exist to be argued with. They are here, in one small file with
 * tests, rather than buried in a service, because the thresholds are the part
 * most likely to need tuning against real shoots.
 */

/** Frames below this share of the shoot's own distribution are candidates for "soft". */
const SOFT_PERCENTILE = 0.15;

/**
 * Below this, a frame is soft no matter what the rest of the shoot looks like.
 *
 * Calibrated against the bundled shoot of fourteen real wildlife photographs,
 * measured on 240px thumbnails:
 *
 *   sharp frames        188 – 3172   (median 1125)
 *   the softest keeper   188         a distant pair on flat water; still usable
 *   blurred by σ=1        83         visibly soft
 *   blurred by σ=2         9         unusable
 *
 * 150 sits in the gap between the softest frame a photographer would still
 * keep and the point where blur is obvious. An earlier value of 60 was below
 * everything in the set, so the flag would never once have fired.
 */
const SOFT_FLOOR = 150;

/** Share of a burst that gets a pick, and the bounds on that count. */
const PICK_SHARE = 0.10;
const MIN_PICKS = 1;
const MAX_PICKS = 4;

/**
 * How sharp a burst's best frame must be, relative to its own shoot, before
 * anything in that burst is worth suggesting.
 *
 * The badge says "sharpest of this burst". On a burst where every frame missed
 * focus that is a true statement and a useless one — and a photographer reads a
 * badge on a photograph as "this one is good", not as a statement about ranking.
 *
 * Measured on the 138-frame shoot that produced the complaint: the two frames
 * reported as wrongly badged ranked 107th and 122nd of 138, and so did every
 * other frame of their burst — 107, 110, 128, 124, 122. The whole run had missed
 * focus. Ranking was never the problem there; the badge simply had no way to say
 * nothing.
 *
 * A percentile of the shoot rather than an absolute number, for the same reason
 * SOFT_PERCENTILE is: variance of the Laplacian has no absolute meaning, only a
 * meaning against other frames of comparable material. A dim, low-contrast shoot
 * would fail any fixed threshold entirely.
 *
 * At the median, on that shoot, the gate refuses both reported badges and costs
 * 14% of badges on bursts of five, 10% on bursts of six, and 36% on pairs —
 * which is the right shape, because a pair of soft frames is exactly the case
 * that should say nothing at all.
 */
const PICK_FLOOR_PERCENTILE = 0.50;

export interface Scored {
  id: number;
  sharpness: number;
}

/**
 * Which frames to flag as soft.
 *
 * Two tests, both of which must pass. Relative alone would flag 15% of a
 * perfectly sharp shoot, which is a lie. Absolute alone would flag nothing in a
 * soft, atmospheric one — also a lie, but a quieter one. Together they flag
 * roughly what a photographer would call soft.
 */
export function softFrames(scored: Scored[]): Set<number> {
  const soft = new Set<number>();
  if (scored.length === 0) return soft;

  const cutoff = percentile(scored.map((s) => s.sharpness), SOFT_PERCENTILE);
  for (const frame of scored) {
    if (frame.sharpness < cutoff && frame.sharpness < SOFT_FLOOR) soft.add(frame.id);
  }
  return soft;
}

/**
 * The sharpness a burst's best frame must clear before anything is suggested.
 *
 * Takes the whole shoot, because the question the gate answers is "sharp for
 * this shoot" — see PICK_FLOOR_PERCENTILE. An empty shoot gates nothing.
 */
export function pickFloor(shoot: Scored[]): number {
  if (shoot.length === 0) return 0;
  return percentile(shoot.map((s) => s.sharpness), PICK_FLOOR_PERCENTILE);
}

export interface PickOptions {
  /** Frames the screening pass flagged. Never suggested, whatever they rank. */
  soft?: ReadonlySet<number>;
  /** From `pickFloor`. A burst whose best frame is under it gets no badge. */
  floor?: number;
}

/**
 * Which frames in one burst to suggest.
 *
 * Scales with the burst: three frames gets one suggestion, forty gets four.
 * A burst of one has nothing to choose between, so it gets no badge at all —
 * a star on a lone frame would say nothing.
 *
 * Two ways of saying nothing, and both matter more than which frame wins:
 *
 *   - Frames the screening pass called soft are not eligible. A suggestion and
 *     a soft warning on one tile contradict each other. Deliberately kept even
 *     though the ranking may come from the better focus measurement and the
 *     flag from the weaker screening one: declining a badge costs a suggestion,
 *     while a tile carrying both markers costs the photographer's trust in
 *     either of them.
 *   - A burst whose best frame does not clear the shoot's floor gets no badge
 *     at all. "The sharpest of these seven" is worthless when all seven missed.
 *
 * Both are the same principle: the badge is a claim about a photograph, so it
 * has to be able to decline to make one.
 */
export function picksForBurst(burst: Scored[], options: PickOptions = {}): Set<number> {
  if (burst.length < 2) return new Set();

  const soft = options.soft ?? new Set<number>();
  const candidates = burst
    .filter((frame) => !soft.has(frame.id))
    .sort((a, b) => b.sharpness - a.sharpness || a.id - b.id);

  if (candidates.length === 0) return new Set();
  if (options.floor !== undefined && candidates[0].sharpness < options.floor) return new Set();

  // Counted against the whole burst, chosen from what is left: forty frames
  // deserve four suggestions whether or not some of them are soft.
  const count = Math.min(MAX_PICKS, Math.max(MIN_PICKS, Math.round(burst.length * PICK_SHARE)));
  return new Set(candidates.slice(0, count).map((frame) => frame.id));
}

/** Linear-interpolated percentile. `p` is 0–1. */
export function percentile(values: number[], p: number): number {
  if (values.length === 0) return 0;

  const sorted = [...values].sort((a, b) => a - b);
  const position = (sorted.length - 1) * p;
  const lower = Math.floor(position);
  const upper = Math.ceil(position);

  if (lower === upper) return sorted[lower];
  return sorted[lower] + (sorted[upper] - sorted[lower]) * (position - lower);
}

export const THRESHOLDS = {
  SOFT_PERCENTILE,
  SOFT_FLOOR,
  PICK_SHARE,
  MIN_PICKS,
  MAX_PICKS,
  PICK_FLOOR_PERCENTILE,
};
